import {
  Box,
  Button,
  Collapse,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { primaryTint } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import Agent from 'services/api/Agent';
import CloseIcon from 'mdi-react/CloseIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import WrenchIcon from 'mdi-react/WrenchIcon';
import SourceBranchIcon from 'mdi-react/SourceBranchIcon';
import AlertCircleIcon from 'mdi-react/AlertCircleIcon';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CHAT_MARKDOWN_DISALLOWED_ELEMENTS } from 'utils/chatMarkdownDisallowedElements';
import remarkStripChatImages from 'utils/remarkStripChatImages';
import { preprocessWhatsAppMarkdown } from 'utils/remarkWhatsAppPreprocess';
import ChatMarkdownLink from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/ChatMarkdownLink';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  drawer: {
    position: 'fixed',
    right: 0,
    top: '64px',
    width: 400,
    backgroundColor: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1200,
    height: 'stretch',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 56,
  },
  title: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
    color: theme.palette.text.primary,
    letterSpacing: '-0.2px',
  },
  intro: {
    padding: theme.spacing(4, 3),
    textAlign: 'center',
    margin: 'auto',
  },
  introText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
  },
  startButton: {
    textTransform: 'none',
    borderRadius: '5px',
    boxShadow: 'none',
    '&:hover': { boxShadow: 'none' },
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    height: 'stretch',
  },
  bubble: {
    maxWidth: '85%',
    padding: theme.spacing(1, 1.5),
    borderRadius: '12px',
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.5,
    wordBreak: 'break-word',
    '& p': { margin: 0 },
    '& p + p': { marginTop: theme.spacing(0.75) },
    '& ul, & ol': { margin: theme.spacing(0.5, 0), paddingLeft: theme.spacing(2.5) },
    '& li': { margin: 0 },
    '& code': {
      backgroundColor: theme.palette.action.subtle,
      padding: '1px 4px',
      borderRadius: '4px',
      fontSize: '0.95em',
    },
    '& pre': {
      backgroundColor: theme.palette.action.subtle,
      padding: theme.spacing(1),
      borderRadius: '6px',
      overflowX: 'auto',
      margin: theme.spacing(0.5, 0),
    },
    '& pre code': { backgroundColor: 'transparent', padding: 0 },
    '& blockquote': {
      borderLeft: `3px solid ${theme.palette.divider}`,
      margin: theme.spacing(0.5, 0),
      padding: theme.spacing(0, 1),
      color: theme.palette.text.secondary,
    },
    '& h1, & h2, & h3, & h4': {
      margin: theme.spacing(1, 0, 0.5),
      fontWeight: 600,
      lineHeight: 1.3,
    },
    '& h1': { fontSize: theme.typography.pxToRem(16) },
    '& h2': { fontSize: theme.typography.pxToRem(15) },
    '& h3': { fontSize: theme.typography.pxToRem(14) },
    '& a': { color: theme.palette.primary.main },
    '& table': {
      borderCollapse: 'collapse',
      margin: theme.spacing(0.5, 0),
      fontSize: theme.typography.pxToRem(12),
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.5, 1),
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(1, 0),
    },
  },
  bubbleUser: {
    alignSelf: 'flex-end',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderBottomRightRadius: 4,
    whiteSpace: 'pre-wrap',
    '& a': { color: theme.palette.primary.contrastText, textDecoration: 'underline' },
  },
  bubbleAssistant: {
    alignSelf: 'flex-start',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: 4,
  },
  toolCallWrapper: {
    alignSelf: 'flex-start',
    maxWidth: '85%',
    width: '100%',
  },
  toolCall: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: '10px',
    backgroundColor: theme.palette.action.subtleLight,
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    overflow: 'hidden',
  },
  skillSwitch: {
    border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
    backgroundColor: primaryTint(theme, 0.08),
  },
  skillSwitchIcon: {
    color: theme.palette.primary.main,
  },
  toolCallHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 1.25),
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': { backgroundColor: primaryTint(theme, 0.06) },
  },
  toolName: {
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.primary,
    letterSpacing: 0.1,
  },
  toolError: { color: theme.palette.error.main },
  toolBody: {
    padding: theme.spacing(0.5, 1.25, 1),
    fontSize: theme.typography.pxToRem(11),
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  toolSection: {
    margin: theme.spacing(0.5, 0),
  },
  toolSectionLabel: {
    fontSize: theme.typography.pxToRem(10),
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: theme.palette.text.secondary,
    marginBottom: '2px',
  },
  toolJson: {
    margin: 0,
    padding: theme.spacing(0.75),
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: theme.palette.text.primary,
  },
  composer: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
  sendButton: {
    textTransform: 'none',
    borderRadius: '5px',
    boxShadow: 'none',
    '&:hover': { boxShadow: 'none' },
  },
});

const formatJson = value => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const toolCallLabel = call => call.announcement?.calling || call.displayName || call.name;

const ToolCallBubble = ({ call, styles }) => {
  const [expanded, setExpanded] = useState(false);
  const isSkillSwitch = call.kind === 'skill_switch' || call.name === 'switch_skill';
  const hasArgs = call.args && Object.keys(call.args).length > 0;
  const hasResult = call.result !== undefined;
  const Icon = call.isError ? AlertCircleIcon : isSkillSwitch ? SourceBranchIcon : WrenchIcon;
  return (
    <Box sx={styles.toolCallWrapper}>
      <Box sx={[styles.toolCall, isSkillSwitch && styles.skillSwitch]}>
        <Box sx={styles.toolCallHeader} onClick={() => setExpanded(e => !e)}>
          {expanded ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
          <Box
            component="span"
            sx={
              call.isError
                ? styles.toolError
                : isSkillSwitch
                  ? styles.skillSwitchIcon
                  : { display: 'inline-flex' }
            }
          >
            <Icon size={14} />
          </Box>
          <Box component="span" sx={[styles.toolName, call.isError && styles.toolError]}>
            {toolCallLabel(call)}
          </Box>
        </Box>
        <Collapse in={expanded} unmountOnExit>
          <Box sx={styles.toolBody}>
            {hasArgs && (
              <Box sx={styles.toolSection}>
                <Box sx={styles.toolSectionLabel}>{I18n.t('AgenticChatFlow.ToolArgs')}</Box>
                <Box component="pre" sx={styles.toolJson}>
                  {formatJson(call.args)}
                </Box>
              </Box>
            )}
            {hasResult && (
              <Box sx={styles.toolSection}>
                <Box sx={styles.toolSectionLabel}>{I18n.t('AgenticChatFlow.ToolResult')}</Box>
                <Box component="pre" sx={styles.toolJson}>
                  {formatJson(call.result)}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

ToolCallBubble.propTypes = {
  call: PropTypes.shape({
    name: PropTypes.string,
    kind: PropTypes.string,
    displayName: PropTypes.string,
    announcement: PropTypes.shape({
      calling: PropTypes.string,
      called: PropTypes.string,
    }),
    args: PropTypes.any,
    result: PropTypes.any,
    isError: PropTypes.bool,
  }).isRequired,
  styles: PropTypes.object.isRequired,
};

const PreviewMarkdownLink = ({ href = null, children = null }) => (
  <ChatMarkdownLink href={href}>{children}</ChatMarkdownLink>
);

PreviewMarkdownLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
};

const previewMarkdownComponents = {
  a: PreviewMarkdownLink,
};

const createMessageId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const PreviewPanel = ({ agentId, open, onClose }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [conversationId, setConversationId] = useState(null);
  const [modality, setModality] = useState('text');
  const [text, setText] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const focusInput = () => {
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (!open) {
      setConversationId(null);
      setModality('text');
      setMessages([]);
      setText('');
    }
  }, [open]);

  useEffect(() => {
    if (open && conversationId) {
      focusInput();
    }
  }, [open, conversationId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const start = async () => {
    setStarting(true);
    try {
      const data = await Agent.previewStart(agentId, { modality });
      setConversationId(data.conversationId);
      const next = [];
      (data.toolCalls || []).forEach(tc =>
        next.push({ id: createMessageId(), role: 'tool', toolCall: tc }),
      );
      (data.openingMessages || []).forEach(line =>
        next.push({ id: createMessageId(), role: 'assistant', text: line }),
      );
      setMessages(next);
    } finally {
      setStarting(false);
    }
  };

  const send = async () => {
    if (!conversationId || !text.trim() || sending) return;
    const userMsg = text;
    const history = messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role, text: m.text }));
    setText('');
    setMessages(m => [...m, { id: createMessageId(), role: 'user', text: userMsg }]);
    setSending(true);
    try {
      const data = await Agent.previewMessage(agentId, conversationId, userMsg, history, {
        modality,
      });
      const next = [];
      (data.toolCalls || []).forEach(tc =>
        next.push({ id: createMessageId(), role: 'tool', toolCall: tc }),
      );
      if (data.reply) next.push({ id: createMessageId(), role: 'assistant', text: data.reply });
      setMessages(m => [...m, ...next]);
    } finally {
      setSending(false);
      focusInput();
    }
  };

  if (!open) return null;

  return (
    <Box sx={styles.drawer}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{I18n.t('AgenticChatFlow.PreviewChat')}</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      {!conversationId ? (
        <Box sx={styles.intro}>
          <Typography sx={styles.introText}>{I18n.t('AgenticChatFlow.PreviewIntro')}</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={modality}
            onChange={(_, value) => {
              if (value) setModality(value);
            }}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="text">
              {I18n.t('AgenticChatFlow.PreviewModalityText')}
            </ToggleButton>
            <ToggleButton value="voice">
              {I18n.t('AgenticChatFlow.PreviewModalityVoice')}
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            sx={styles.startButton}
            variant="contained"
            color="primary"
            onClick={start}
            disabled={starting}
          >
            {I18n.t('AgenticChatFlow.StartPreview')}
          </Button>
        </Box>
      ) : (
        <>
          <Box sx={styles.messages} ref={scrollRef}>
            {messages.map(m => {
              if (m.role === 'tool') {
                return <ToolCallBubble key={m.id} call={m.toolCall} styles={styles} />;
              }
              return (
                <Box
                  key={m.id}
                  sx={[
                    styles.bubble,
                    m.role === 'user' ? styles.bubbleUser : styles.bubbleAssistant,
                  ]}
                >
                  {m.role === 'assistant' ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkStripChatImages]}
                      disallowedElements={CHAT_MARKDOWN_DISALLOWED_ELEMENTS}
                      components={previewMarkdownComponents}
                    >
                      {preprocessWhatsAppMarkdown(m.text || '')}
                    </ReactMarkdown>
                  ) : (
                    m.text
                  )}
                </Box>
              );
            })}
          </Box>
          <Box sx={styles.composer}>
            <TextField
              fullWidth
              size="small"
              inputRef={inputRef}
              autoFocus
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={I18n.t('AgenticChatFlow.TypeMessage')}
              disabled={sending}
            />
            <Button
              sx={styles.sendButton}
              variant="contained"
              color="primary"
              onClick={send}
              disabled={sending || !text.trim()}
            >
              {I18n.t('AgenticChatFlow.Send')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

PreviewPanel.propTypes = {
  agentId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PreviewPanel;
